import type { SplitButtonState } from "@fluentui/react-components";
import {
  useSplitButtonStyles_unstable,
  makeStyles,
  mergeClasses,
  shorthands,
  splitButtonClassNames,
  createCustomFocusIndicatorStyle,
} from "@fluentui/react-components";

// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastRestStyles,
  commonHighContrastDisabledStyles,
  HighContrastSelector,
} from "@1js/acui-common/lib/styling/next/commonStyles.styles";

// eslint-disable-next-line no-restricted-imports
import type { AppSplitButtonState } from "@1js/acui-button/lib/Next";
import { getRibbonButtonSizedStyles } from "../../next/commonRibbonButtonStyles.styles";
import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";
import { ActionControlSize } from "@1js/acui-common";

const useRootStyles = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...commonHighContrastRestStyles,
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...commonHighContrastRestStyles,
        ":focus": {
          ...commonHighContrastRestStyles,
        },
      },
    },
    ":hover": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralLighter),
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralLighter),
      },
    },
    ":active": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralQuaternaryAlt),
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralQuaternaryAlt),
      },
    },
  },
  disabled: {
    [`${HighContrastSelector}`]: {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...commonHighContrastDisabledStyles,
        ...commonHighContrastRestStyles,
      },
      ":hover": {
        [`& .${splitButtonClassNames.primaryActionButton}`]: {
          ...commonHighContrastDisabledStyles,
          ...commonHighContrastRestStyles,
        },
        [`& .${splitButtonClassNames.menuButton}`]: {
          ...commonHighContrastDisabledStyles,
          ...commonHighContrastRestStyles,
        },
      },
    },
  },
  large: {
    flexDirection: "column",
    minWidth: "40px",

    // Have to do this because of Fluent style in useSplitButtonStyles
    // Specificity is greater than a regular class name on the MenuButton
    [`& .${splitButtonClassNames.menuButton}`]: {
      borderLeftWidth: "1px",
    },
  },
});

const useRootStylesNext = makeStyles({
  large: {
    flexDirection: "column",
    minWidth: "40px",
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      borderBottomLeftRadius: customTokens.borderRadiusNone,
      borderBottomRightRadius: customTokens.borderRadiusNone,
      borderTopRightRadius: customTokens.borderRadiusMedium,
      borderRightWidth: customTokens.strokeWidthThin,
      borderRightColor: customTokens.colorTransparentBackground,
    },
    ":hover, :hover:active": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        borderRightColor: customTokens.colorTransparentBackground,
      },
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      borderLeftWidth: customTokens.strokeWidthThin,
      borderTopLeftRadius: customTokens.borderRadiusNone,
      borderTopRightRadius: customTokens.borderRadiusNone,
      borderBottomLeftRadius: customTokens.borderRadiusMedium,
    },
  },
});

const useFocusStyles = makeStyles({
  primaryActionButton: createCustomFocusIndicatorStyle({
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderTopRightRadius: customTokens.borderRadiusMedium,
  }),
  menuButton: createCustomFocusIndicatorStyle({
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: customTokens.borderRadiusMedium,
  }),
});

const useFocusStylesNext = makeStyles({
  primaryActionButton: createCustomFocusIndicatorStyle({
    borderBottomLeftRadius: customTokens.borderRadiusNone,
    borderBottomRightRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusMedium,
  }),
  menuButton: createCustomFocusIndicatorStyle({
    borderTopWidth: 0,
    borderTopLeftRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusNone,
    borderLeftWidth: customTokens.strokeWidthThin,
    borderBottomLeftRadius: customTokens.borderRadiusMedium,
  }),
});

const useToggledRibbonSplitButtonStyles = makeStyles({
  base: {
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      ...shorthands.borderColor(customTokens.legacy_neutralLight),
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      ...shorthands.borderColor(customTokens.legacy_neutralLight),
    },
  },
});

const useFocusHighContrastStylesNext = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      ":focus": {
        outlineStyle: "solid",
      },
      ":focus-visible": {
        outlineStyle: "solid",
      },
    },
  },
});

export const useRibbonSplitButtonStyles = (state: AppSplitButtonState) => {
  const { toggleable, toggled } = state;

  const rootStyles = useRootStyles();
  const toggledRootStyles = useToggledRibbonSplitButtonStyles();
  const focusStyles = useFocusStyles();
  const rootStylesNext = useRootStylesNext();
  const focusStylesNext = useFocusStylesNext();
  const focusHighContrastStylesNext = useFocusHighContrastStylesNext();

  if (isNextStyleEnabled()) {
    state.root.className = mergeClasses(
      getRibbonButtonSizedStyles(state.size, rootStylesNext),
      toggleable && toggled && toggledRootStyles.base,
      state.root.className
    );

    // Large buttons are vertical so we need to change the default border radius
    if (
      state.menuButton &&
      state.size === ActionControlSize.Large.toLowerCase()
    ) {
      state.menuButton.className = mergeClasses(
        focusStylesNext.menuButton,
        state.menuButton.className
      );
    }

    // Large buttons are vertical so we need to change the default border radius
    if (
      state.primaryActionButton &&
      state.size === ActionControlSize.Large.toLowerCase()
    ) {
      state.primaryActionButton.className = mergeClasses(
        focusStyles.primaryActionButton,
        state.primaryActionButton.className
      );
    }

    // 8072142: Fix the issue where a high contrast split button wouldn't show it's focus outline properly
    if (state.primaryActionButton) {
      state.primaryActionButton.className = mergeClasses(
        state.primaryActionButton.className,
        focusHighContrastStylesNext.base
      );
    }

    // 8072142: Fix the issue where a high contrast split button wouldn't show it's focus outline properly
    if (state.menuButton) {
      state.menuButton.className = mergeClasses(
        state.menuButton.className,
        focusHighContrastStylesNext.base
      );
    }
  } else {
    state.root.className = mergeClasses(
      rootStyles.base,
      getRibbonButtonSizedStyles(state.size, rootStyles),
      (state.disabled || state.disabledFocusable) && rootStyles.disabled,
      toggleable && toggled && toggledRootStyles.base,
      state.root.className
    );
  }

  useSplitButtonStyles_unstable(state as SplitButtonState);

  return state;
};
